/**
 * Маркетинговая атрибуция и идентичность (TRK-4, AUTH-7).
 * Ключи localStorage совпадают с текущим SPA и лендингом — один origin.
 */
import { uuidv4 } from '@/lib/ids';
import { local, session } from '@/lib/storage';
import type { RegistrationAttribution } from '@/api/types';

const VISITOR_ID_KEY = 'shotbook_visitor_id';
const VISITOR_COOKIE = 'visitor_id';
const ATTRIBUTION_KEY = 'shotbook_attribution';
const SESSION_ID_KEY = 'shotbook_session_id';
const SESSION_REFERRER_SENT_KEY = 'shotbook_referrer_sent';
const GUEST_VISITED_KEY = 'guest_visited';

const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UtmField = (typeof UTM_FIELDS)[number];

export type Attribution = Partial<Record<UtmField, string>> & {
  visitor_id?: string;
  referrer?: string;
  landing_page?: string;
};

const isBrowser = () => typeof window !== 'undefined';
const VALID_ID = /^[a-zA-Z0-9-]{1,36}$/;

function readVisitorCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${VISITOR_COOKIE}=([^;]+)`));
  const value = match?.[1];
  // Битое значение игнорируем — decodeURIComponent бросал бы синхронно во всех точках трекинга.
  return value && VALID_ID.test(value) ? value : null;
}

function writeVisitorCookie(id: string): void {
  if (typeof document === 'undefined') return;
  // JS-кука — мост: долгоживущую ставит бэкенд в ответе /guest-visit.
  const domain = /(^|\.)shotbook\.art$/.test(window.location.hostname)
    ? '; Domain=.shotbook.art'
    : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${VISITOR_COOKIE}=${encodeURIComponent(id)}; Max-Age=34560000; Path=/${domain}; SameSite=Lax${secure}`;
}

function stripVisitorIdFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('visitor_id');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Не смогли почистить адрес — не страшно.
  }
}

/**
 * visitor_id: кука → localStorage → URL → генерация (TRK-4). Своя identity
 * первее URL: ссылку с ?visitor_id копируют и шарят, без этого кликнувший
 * сливался бы с автором ссылки. URL принимается только при отсутствии своей
 * identity и тут же вычищается из адреса.
 */
export function getOrCreateVisitorId(): string {
  if (!isBrowser()) return '';

  const fromCookie = readVisitorCookie();
  const fromStorage = local.get(VISITOR_ID_KEY);
  let id = fromCookie || fromStorage;

  if (!id) {
    const fromUrl = new URL(window.location.href).searchParams.get('visitor_id');
    if (fromUrl && VALID_ID.test(fromUrl)) {
      id = fromUrl;
      stripVisitorIdFromUrl();
    }
  }

  id = id || uuidv4();
  if (fromStorage !== id) local.set(VISITOR_ID_KEY, id);
  if (fromCookie !== id) writeVisitorCookie(id);
  return id;
}

/** UUID вкладки: одна вкладка — один session_id; уходит заголовком X-Session-Id. */
export function getOrCreateSessionId(): string {
  if (!isBrowser()) return '';
  let id = session.get(SESSION_ID_KEY);
  if (!id) {
    id = uuidv4();
    session.set(SESSION_ID_KEY, id);
  }
  return id;
}

export const newEventId = uuidv4;

/** document.referrer отдаётся один раз за заход вкладки. */
export function takeSessionReferrer(): string | undefined {
  if (!isBrowser()) return undefined;
  if (session.get(SESSION_REFERRER_SENT_KEY)) return undefined;
  session.set(SESSION_REFERRER_SENT_KEY, '1');
  return document.referrer || undefined;
}

export function clearGuestSessionFlags(): void {
  session.remove(GUEST_VISITED_KEY);
}

export function hasGuestVisitBeenSent(): boolean {
  return !!session.get(GUEST_VISITED_KEY);
}

export function markGuestVisitSent(): void {
  session.set(GUEST_VISITED_KEY, '1');
}

/** Захват UTM/landing_page из URL (last-touch); без новых данных — прежнее значение. */
export function captureAttributionFromUrl(): Attribution {
  if (!isBrowser()) return {};

  const url = new URL(window.location.href);
  const captured: Attribution = {};
  let hasUtm = false;

  for (const field of UTM_FIELDS) {
    const value = url.searchParams.get(field);
    if (value) {
      captured[field] = value.slice(0, 255);
      hasUtm = true;
    }
  }

  const landingPage = url.searchParams.get('landing_page');
  if (landingPage) captured.landing_page = landingPage.slice(0, 255);

  const existing = readAttribution();
  if (!hasUtm && !landingPage && existing) return existing;

  captured.referrer = document.referrer || existing?.referrer;
  if (!captured.landing_page && existing?.landing_page)
    captured.landing_page = existing.landing_page;

  local.setJson(ATTRIBUTION_KEY, captured);
  return captured;
}

export function readAttribution(): Attribution | null {
  return local.getJson<Attribution>(ATTRIBUTION_KEY);
}

/** Полный пакет для /register и /auth/google (AUTH-7). */
export function getRegistrationAttribution(): RegistrationAttribution {
  return { visitor_id: getOrCreateVisitorId(), ...(readAttribution() ?? {}) };
}

/** После привязки к пользователю; visitor_id остаётся. */
export function clearAttribution(): void {
  local.remove(ATTRIBUTION_KEY);
}
